import { isEqual } from "lodash";
import { useRef, useState } from "react";
import { Flex, Text } from "@theorgchartdev/storybook";
import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { PaginationConfig } from "@/appComponents/Blocks/Composites/PaginationConfig/PaginationConfig";
import ConfirmationPopover from "@/appComponents/Blocks/Composites/ConfirmationPopover/ConfirmationPopover";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { NormalizeSCWiz } from "./Optimize.utils";
import { appstr } from "@coreapp/utils/appstr";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";

type PaginationWizardTabProps = {
  doc: QXDoc;
};

export const PaginationWizardTab = ({ doc }: PaginationWizardTabProps) => {
  const [scWiz, setScWiz] = useState(() => doc.GetIWizData().Clone());
  const scConfig = NormalizeSCWiz(scWiz);

  useChartDocumentContext();

  const scm = doc?.GetSubChartMgr();
  const hasBreakOverrides = scm?.HasBreakOverrides();
  const isDirty = !isEqual(scConfig, NormalizeSCWiz(doc.GetIWizData()));
  const isActionable = isDirty || hasBreakOverrides;

  const confirmRef = useRef(null);
  const fieldNames = (doc as any)?.GetTargetHeaders();

  const handleUpdate = (key: string, value?: unknown) => {
    switch (key) {
      case "scChartBreakType":
        scWiz.SetSubChartOption(value);
        break;
      case "startbreaksat":
        scWiz.SetSmartStartLevel(`Level ${value}`);
        break;
      case "nameusing":
        scWiz.SetSubChartNameFieldID(value);
        break;
      case "pagedensity":
        scWiz.SetPageDensity(value);
        break;
      case "breakevery":
        scWiz.SetBreakLevels(`${value} Levels`);
        break;
      case "breakfield":
        scWiz.SetBreakField(value);
        break;
      case "minsubsforbreak":
        scWiz.SetSmartBreak(`${value} Boxes`);
        break;
      default:
        break;
    }
    setScWiz(scWiz.Clone());
  };

  const handleApply = () => {
    const w = GetWSMgr();
    if (!scm || !w) return;

    scm.ClearBreakOverrides();

    const targetWiz = doc.GetIWizData();
    targetWiz.SetSubChartOption(scWiz.GetSubChartOption());
    targetWiz.SetSmartStartLevel(scWiz.GetSmartStartLevel());
    targetWiz.SetSubChartNameFieldID(scWiz.GetSubChartNameFieldID());
    targetWiz.SetPageDensity(scWiz.GetPageDensity());
    targetWiz.SetBreakLevels(scWiz.GetBreakLevels());
    targetWiz.SetBreakField(scWiz.GetBreakField());
    targetWiz.SetSmartBreak(scWiz.GetSmartBreak());

    w.ClearUndoRedo();
    w.HandleApplySubChartWiz();
    scm.CopySubChartNames(doc);

    setScWiz(doc.GetIWizData().Clone());
    ReRenderCanvas();
  };

  const handleCancel = () => {
    setScWiz(doc.GetIWizData().Clone());
  };

  return (
    <Flex direction="column" height="100%">
      <div className="ocn-sidebar-section-container" style={{ flex: 1, overflowY: "auto" }}>
        <PaginationConfig
          scConfig={scConfig}
          handleUpdate={handleUpdate}
          useSettingModalStyle={false}
          fieldNames={fieldNames}
        />
      </div>
      <RSBFooter>
        <RSBSecondaryButton onClick={handleCancel} disabled={!isActionable}>
          {appstr("Cancel")}
        </RSBSecondaryButton>
        <ConfirmationPopover
          onSubmit={handleApply}
          openCtrlRef={confirmRef}
          otherHeaderProps={{
            title: appstr("Apply pagination settings?")
          }}
          confirmLabel={appstr("Apply")}
          dataTestPrefix={"pagination-confirm"}
          placement={"top"}
          popoverProps={{
            enabled: hasBreakOverrides,
            reference: (
              <RSBPrimaryButton
                onClick={!hasBreakOverrides ? handleApply : undefined}
                disabled={!isActionable}
                dataTestId={"pagination-btn-apply"}
              >
                {appstr("Apply to chart")}
              </RSBPrimaryButton>
            )
          }}
        >
          <Text size="small">
            {appstr("Applying these settings will remove any manual subchart breaks")}
          </Text>
        </ConfirmationPopover>
      </RSBFooter>
    </Flex>
  );
};
